// Modale per invio documenti HSE via email
import { useState } from 'react'
import { Send, X, FileText, Link, Paperclip } from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { useSendDocuments } from '../../../hooks/useHse'

export interface DocToSend {
  type: 'certification' | 'safety_doc'
  id: string
  fileName: string
  label: string  // tipo documento leggibile
}

interface SendDocumentsModalProps {
  isOpen: boolean
  onClose: () => void
  preselected: DocToSend[]
}

type DeliveryMode = 'attachment' | 'link' | 'both'

const DELIVERY_OPTIONS: Array<{ value: DeliveryMode; label: string; icon: React.ReactNode }> = [
  { value: 'attachment', label: 'Allegati PDF', icon: <Paperclip className="h-4 w-4" /> },
  { value: 'link', label: 'Link download (7gg)', icon: <Link className="h-4 w-4" /> },
  { value: 'both', label: 'Entrambi', icon: <><Paperclip className="h-3 w-3" /><Link className="h-3 w-3" /></> },
]

export function SendDocumentsModal({ isOpen, onClose, preselected }: SendDocumentsModalProps) {
  const today = new Date().toLocaleDateString('it-IT')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [subject, setSubject] = useState(`Documentazione HSE - ${today}`)
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('both')
  const [docs, setDocs] = useState<DocToSend[]>(preselected)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const sendMutation = useSendDocuments()

  const removeDoc = (id: string) => setDocs((prev) => prev.filter((d) => d.id !== id))

  const handleSend = async () => {
    setSuccessMsg('')
    setErrorMsg('')
    try {
      await sendMutation.mutateAsync({
        recipientEmail,
        subject,
        deliveryMode,
        documents: docs.map((d) => ({ type: d.type, id: d.id })),
      })
      setSuccessMsg(`Email inviata a ${recipientEmail}`)
      setRecipientEmail('')
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Errore durante l\'invio')
    }
  }

  const canSend =
    recipientEmail.includes('@') &&
    docs.length > 0 &&
    !sendMutation.isPending

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-xl bg-[var(--fc-surface)] shadow-2xl border border-[var(--fc-border)] p-6 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--fc-foreground)] flex items-center gap-2">
            <Send className="h-5 w-5 text-[var(--fc-primary)]" />
            Invia documenti
          </h2>
          <button onClick={onClose} className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Documenti selezionati */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-[var(--fc-foreground-muted)]">Documenti ({docs.length})</p>
          {docs.length === 0 && (
            <p className="text-sm text-[var(--fc-foreground-subtle)] italic">Nessun documento selezionato</p>
          )}
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center justify-between rounded-lg bg-[var(--fc-surface-raised)] px-3 py-2 text-sm">
                <span className="flex items-center gap-2 text-[var(--fc-foreground)]">
                  <FileText className="h-4 w-4 text-[var(--fc-primary)]" />
                  <span className="font-medium">{d.label}</span>
                  <span className="text-[var(--fc-foreground-muted)] truncate max-w-[140px]">{d.fileName}</span>
                </span>
                <button
                  onClick={() => removeDoc(d.id)}
                  className="ml-2 text-[var(--fc-foreground-subtle)] hover:text-[var(--fc-danger)]"
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Email destinatario */}
        <Input
          label="Email destinatario"
          type="email"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          placeholder="cliente@example.com"
        />

        {/* Oggetto */}
        <Input
          label="Oggetto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />

        {/* Modalità invio */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-[var(--fc-foreground-muted)]">Modalità invio</p>
          <div className="flex gap-2">
            {DELIVERY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDeliveryMode(opt.value)}
                className={`flex-1 flex items-center justify-center gap-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  deliveryMode === opt.value
                    ? 'border-[var(--fc-primary)] bg-[var(--fc-primary)]/10 text-[var(--fc-primary)] font-medium'
                    : 'border-[var(--fc-border)] text-[var(--fc-foreground-muted)] hover:border-[var(--fc-foreground-muted)]'
                }`}
              >
                {opt.icon}
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Messaggi stato */}
        {successMsg && (
          <p className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">
            {successMsg}
          </p>
        )}
        {errorMsg && (
          <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {errorMsg}
          </p>
        )}

        {/* Azioni */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button
            leftIcon={<Send className="h-4 w-4" />}
            onClick={handleSend}
            disabled={!canSend}
            isLoading={sendMutation.isPending}
          >
            Invia email
          </Button>
        </div>
      </div>
    </div>
  )
}
